import React, { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton, Button, Slide, TextField, DialogTitle, DialogContent, DialogActions, Dialog, useTheme, ThemeProvider, createTheme, FormHelperText } from "@mui/material";
import styles from "../styles/AddEditUserModal.module.css";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Checkbox from "@mui/material/Checkbox";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";
import { useDispatch } from "../store";
import { useLocation, useParams } from "react-router-dom";
import { useAddRoleMutation, useUpdateRoleMutation } from "store/slices/apis/rolePackageSlice";
import {ReactComponent as CrossIcon } from "assets/PwC_Funct_Icons_Close_Outline_Black_RGB.svg"


const checkBoxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkBoxCheckedIcon = <CheckBoxIcon fontSize="small" />;

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const AddEditRoleModal = ({ open, handleClose, user, onSubmit, type }) => {
    const { projectId } = useParams();
    const newtheme = useTheme();
    const dispatch = useDispatch();
    const formikRef = useRef(null);
    const location = useLocation();

    const [addRole, { isLoading, error }] = useAddRoleMutation();
    const [updateRole, { isLoadingedit, erroredit }] = useUpdateRoleMutation();

    useEffect(() => {
        if (open && !user && formikRef.current) {
            formikRef.current.resetForm(); // Reset form to clear previous values
        }
    }, [open, user]);

    const theme = createTheme({
        components: {
            MuiTextField: {
                styleOverrides: {
                    root: {
                        border: "2px solid #e6e6e6",
                        borderRadius: "4px",
                        marginTop: "16px",
                        "& .MuiFilledInput-input": {
                            backgroundColor: "#fff",
                            color: "#252525",
                            fontSize: "16px",
                        },
                        "& .MuiFilledInput-root::before": {
                            borderBottom: "none",
                        },
                        "& .MuiFilledInput-root::after": {
                            borderBottom: `2px solid ${newtheme?.customSettings?.themeColor}`,
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                            color: "#696969",
                            fontSize: "14px",
                        },
                        "& .MuiFormLabel-root": {
                            color: "#696969",
                            fontSize: "15px",
                        },
                        "& .MuiFilledInput-root.Mui-disabled:before": {
                            borderBottom: "none",
                        },
                        "& .MuiFilledInput-root": {
                            backgroundColor: "#fff",
                        },
                        "& .MuiFilledInput-input": {
                            backgroundColor: "none",
                            fontSize: "14px",
                            color: "#252525",
                        },
                        "& .MuiInputLabel-root.Mui-disabled": {
                            color: "#696969",
                        },
                        "& .MuiFilledInput-root.Mui-disabled": {
                            backgroundColor: "#fff",
                            color: "inherit",
                        },
                        "& .MuiFilledInput-input.Mui-disabled": {
                            WebkitTextFillColor: "#696969",
                            opacity: "none",
                        },
                        "& .MuiAutocomplete-tag": {
                            margin: "10px 3px 5px 3px !important",
                        },
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    option: {
                        '&[aria-selected="true"]': {
                            backgroundColor: "transparent",
                        },

                        "&:hover": {
                            backgroundColor: "transparent",
                        },
                    },
                },
            },
        },
    });

    const validationSchema = Yup.object().shape({
        displayName: Yup.string()
            .matches(
                /^(?!\d+$)[A-Za-z0-9\s]+$/,
                "Role Name doesn't only contain numbers"
            )
            .required("Please enter Role Name"),
    });

    const openNotification = async (msg, type = "error") => {
        await dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: "alert",
                alert: { color: type },
                close: false,
            })
        );

        setTimeout(() => dispatch(closeSnackbar()), 10000);
    };

    const handleSubmit = async (values) => {
        const submittedValues = {
            Name: values.displayName?.trim(),
            ProjectId: projectId,
        };

        if (user?.id) {
            submittedValues.Id = user.id;
            try {
                const response = await updateRole(submittedValues).unwrap();
                if (response?.status === "success") {
                    openNotification(response?.message || "Role updated successfully.", "success");
                } else {
                    openNotification(response?.message || "Update failed.");
                }
            } catch (err) {
                let msg = "Something went wrong.";
                if (err && typeof err === "object") {
                    // FetchBaseQueryError
                    if ("status" in err) {
                      // JSON message
                      if (err.data?.message) msg = err.data.message;
                      // string body (some APIs return plain text)
                      else if (typeof err.data === "string") msg = err.data;
                      // fallback with status code
                      else if (typeof err.status === "number") msg = `Request failed (${err.status}).`;
                    }
                    // SerializedError (network, etc.)
                    else if ("message" in err && err.message) {
                      msg = err.message;
                    }
                  }
                  openNotification(msg, "error");
                
            }

        } else {

            try {
                const response = await addRole(submittedValues).unwrap();

                if (response?.status === 'success') {
                    openNotification(response?.message || "Role added successfully.", 'success');
                }
                else {
                    openNotification(response?.message || "Add failed.");
                }
            } catch (err) {
                let msg = "Something went wrong.";
                if (err && typeof err === "object") {
                    // FetchBaseQueryError
                    if ("status" in err) {
                      // JSON message
                      if (err.data?.message) msg = err.data.message;
                      // string body (some APIs return plain text)
                      else if (typeof err.data === "string") msg = err.data;
                      // fallback with status code
                      else if (typeof err.status === "number") msg = `Request failed (${err.status}).`;
                    }
                    // SerializedError (network, etc.)
                    else if ("message" in err && err.message) {
                      msg = err.message;
                    }
                  }
                  openNotification(msg, "error");
            }
        }
        onSubmit();
    };

    const handleCloseModal = () => {
        formikRef.current?.resetForm();
        handleClose();
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleCloseModal();
                }
            }}
            aria-describedby="add-user-modal-description"
            sx={{
                "& .MuiDialog-paper": {
                    maxWidth: "550px",
                    width: "100%",
                },
            }}
        >
            <DialogTitle>
                <h3 className={styles.heading}>
                    {user ? "Edit Role" : "Add Role"}
                </h3>
                <IconButton
                    sx={{ position: "absolute", right: 8, top: 8 }}
                    onClick={handleCloseModal}
                >
                    {/* <CloseIcon /> */}
                    <CrossIcon style={{ fontSize: "20px", width:"20px", height:"20px", fill:"#808080" }} />
                </IconButton>
            </DialogTitle>
            <Formik
                innerRef={formikRef}
                initialValues={{
                    displayName: user?.displayName || "",
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    setFieldValue,
                    setTouched,
                }) => (
                    <>
                        <Form>
                            <DialogContent
                                sx={{
                                    padding: "0px 24px 20px 24px",
                                }}
                            >
                                <ThemeProvider theme={theme}>
                                    <TextField
                                        label={
                                            <span>
                                                Role Name{" "}
                                                <span style={{ color: "#C52A1A", fontSize: "20px" }}>
                                                    *
                                                </span>
                                            </span>
                                        }
                                        name="displayName"
                                        value={values.displayName}
                                        onChange={handleChange}
                                        fullWidth
                                        variant="filled"
                                        onBlur={() => setTouched({ ...touched, displayName: true })}
                                        autoComplete="off"
                                    />
                                    {touched?.displayName && errors?.displayName && (
                                        <FormHelperText
                                            sx={{ color: "#C52A1A", fontSize: "12px" }}
                                        >{`${errors?.displayName}`}</FormHelperText>
                                    )}
                                </ThemeProvider>
                            </DialogContent>
                            <DialogActions
                                sx={{
                                    marginBottom: "20px",
                                }}
                            >
                                {/* <Button
                                    onClick={handleCloseModal}
                                    variant="outlined"
                                    sx={{
                                        border: `1px solid ${newtheme?.customSettings?.themeColor}`,
                                        color: newtheme?.customSettings?.themeColor,
                                        fontSize: "16px",
                                        fontWeight: 500,
                                        lineHeight: "24px",
                                        textTransform: "none",
                                    }}
                                >
                                    Cancel
                                </Button> */}
                                <Button
                                    type="submit"
                                    variant="contained"
                                    sx={{
                                        backgroundColor: `${newtheme?.customSettings?.themeColor}`,
                                        border: `1px solid ${newtheme?.customSettings?.themeColor}`,
                                        color: "#fff",
                                        fontSize: "16px",
                                        fontWeight: 500,
                                        lineHeight: "24px",
                                        marginRight: "20px",
                                        textTransform: "none",
                                    }}
                                >
                                    Save
                                </Button>
                            </DialogActions>
                        </Form>
                    </>
                )}
            </Formik>
        </Dialog>
    );
};

export default AddEditRoleModal;
